import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Leaf, Loader2, Lock, AlertCircle, CheckCircle2 } from 'lucide-react';

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!token) { setErr(t('resetPassword.invalidToken')); return; }
    if (pw.length < 6) { setErr(t('auth.passwordTooShort')); return; }
    if (pw !== confirm) { setErr(t('auth.passwordsDoNotMatch')); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || t('resetPassword.invalidToken'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary">
            <Leaf className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-heading font-bold text-2xl tracking-tight">{t('app.name')}</span>
        </div>

        <Card className="p-8 shadow-lg border-border">
          <div className="mb-6">
            <h1 className="text-2xl font-bold tracking-tight">{t('resetPassword.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('resetPassword.subtitle')}</p>
          </div>

          {done ? (
            <div className="flex items-start gap-2 p-3 rounded-md bg-green-500/10 border border-green-500/30 text-sm" data-testid="reset-success-panel">
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
              <span>{t('resetPassword.success')}</span>
            </div>
          ) : (
            <>
              {err && (
                <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-sm" data-testid="reset-error">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{err}</span>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="pw">{t('resetPassword.newPassword')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="pw" data-testid="reset-password-input" type="password" value={pw}
                           onChange={(e) => setPw(e.target.value)} placeholder="••••••••"
                           className="pl-9 rtl:pl-3 rtl:pr-9" autoComplete="new-password" required />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm">{t('resetPassword.confirmNewPassword')}</Label>
                  <Input id="confirm" data-testid="reset-confirm-input" type="password" value={confirm}
                         onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••"
                         autoComplete="new-password" required />
                </div>
                <Button type="submit" disabled={loading} className="w-full" data-testid="reset-submit-btn">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('resetPassword.resetting')}</> : t('resetPassword.reset')}
                </Button>
              </form>
            </>
          )}

          <div className="mt-6 pt-6 border-t border-border text-center text-sm">
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="back-to-signin-link">
              ← {t('resetPassword.backToSignIn')}
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
